import { Alert, AlertTitle, Button, Box, CircularProgress, Collapse, useTheme, alpha } from '@mui/material';
import { WifiOff, Refresh, Close } from '@mui/icons-material';
import { useConnection } from '../../context/ConnectionContext';
import { ConnectionErrorType } from '../../services/connectionService';

/**
 * Connection Banner Component
 * Displays a banner at the top of the app when connection issues are detected
 */
const ConnectionBanner = () => {
  const theme = useTheme();
  const {
    isConnected,
    isChecking,
    showBanner,
    errorType,
    errorMessage,
    retryCount,
    manualRetry,
    dismissBanner
  } = useConnection();

  // Don't show banner if connected or explicitly dismissed
  if (isConnected || !showBanner) {
    return null;
  }

  // Determine severity based on error type
  const getSeverity = () => {
    switch (errorType) {
      case ConnectionErrorType.NETWORK_ERROR:
        return 'error';
      case ConnectionErrorType.BACKEND_UNAVAILABLE:
        return 'error';
      case ConnectionErrorType.BACKEND_DEGRADED:
        return 'warning';
      case ConnectionErrorType.TIMEOUT:
        return 'warning';
      default:
        return 'error';
    }
  };

  // Get appropriate icon
  const getIcon = () => {
    return <WifiOff />;
  };

  // Get title based on error type
  const getTitle = () => {
    switch (errorType) {
      case ConnectionErrorType.NETWORK_ERROR:
        return 'Sin conexión a Internet';
      case ConnectionErrorType.BACKEND_UNAVAILABLE:
        return 'Servidor no disponible';
      case ConnectionErrorType.BACKEND_DEGRADED:
        return 'Servicio limitado';
      case ConnectionErrorType.TIMEOUT:
        return 'Conexión lenta';
      default:
        return 'Error de conexión';
    }
  };

  const handleRetry = async () => {
    await manualRetry();
  };

  return (
    <Collapse in={showBanner}>
      <Alert
        severity={getSeverity()}
        icon={getIcon()}
        sx={{
          borderRadius: 0,
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 9999,
          boxShadow: 2,
          '& .MuiAlert-message': {
            width: '100%'
          }
        }}
        action={
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button
              color="inherit"
              size="small"
              onClick={handleRetry}
              disabled={isChecking}
              startIcon={isChecking ? <CircularProgress size={16} /> : <Refresh />}
              sx={{
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              {isChecking ? 'Verificando...' : 'Reintentar'}
            </Button>
            <Button
              color="inherit"
              size="small"
              onClick={dismissBanner}
              sx={{ minWidth: 'auto', p: 0.5 }}
            >
              <Close fontSize="small" />
            </Button>
          </Box>
        }
      >
        <AlertTitle sx={{ fontWeight: 700, mb: 0.5 }}>
          {getTitle()}
        </AlertTitle>
        {errorMessage}

        {/* Development mode: Show technical details */}
        {process.env.NODE_ENV === 'development' && errorType === ConnectionErrorType.BACKEND_DEGRADED && (
          <Box
            component="span"
            sx={{
              display: 'block',
              mt: 1,
              fontSize: '0.85rem',
              fontFamily: 'monospace',
              backgroundColor: alpha(theme.palette.text.primary, 0.1),
              padding: '4px 8px',
              borderRadius: '4px',
              color: 'inherit'
            }}
          >
            🔧 DEV: Servicio degradado - La base de datos no está disponible. Verifica la conexión a PostgreSQL.
          </Box>
        )}

        {retryCount > 0 && (
          <Box
            component="span"
            sx={{
              display: 'block',
              mt: 0.5,
              fontSize: '0.85rem',
              opacity: 0.8
            }}
          >
            Reintentando automáticamente... (intento {retryCount})
          </Box>
        )}
      </Alert>
    </Collapse>
  );
};

export default ConnectionBanner;
